import { Config, ConfigKeys } from '~/Config';
import { type ApiName, type Environment } from '~/constants';

const { hostname } = globalThis.location;

export const isLocalhost = Boolean(
  hostname === 'localhost' ||
  // [::1] is the IPv6 localhost address.
  hostname === '[::1]' ||
  // 127.0.0.1/8 is considered localhost for IPv4.
  /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.test(hostname),
);

/**
 * Maps environment to its abbreviated name.
 */
export const ENVIRONMENT_ABBREVIATED_NAME_MAP: Record<Environment, ApiName> = {
  beta: 'BETA',
  development: 'DEV',
  local: 'LOCAL',
  production: 'PROD',
  staging: 'STAGING',
  test: 'TEST',
} as const;

/**
 * Maps environment to API name.
 */
export const ENVIRONMENT_API_NAME_MAP: Record<ApiName, ApiName> = {
  BETA: 'PROD',
  DEV: 'DEV',
  LOCAL: 'LOCAL',
  PROD: 'PROD',
  STAGING: 'STAGING',
  TEST: 'TEST',
} as const;

const SUBDOMAIN_TO_ENVIRONMENT: Record<string, Environment> = {
  beta: 'beta',
  dev: 'development',
  dev2: 'development',
  dev3: 'development',
  dev4: 'development',
  local: 'local',
  production: 'production',
  staging: 'staging',
  test: 'test',
};

function environmentFromApiUrl(url: string): Environment {
  if (url.includes('localhost')) {
    return 'local';
  }

  const subdomain =
    /app\.([^.]+)\.vestigas/.exec(url)?.[1]?.toLowerCase() ?? 'production';

  return SUBDOMAIN_TO_ENVIRONMENT[subdomain] ?? 'production';
}

/**
 * Get API environment name from URL subdomain.
 */
export const getApiFromSubdomain = (url: string): ApiName =>
  ENVIRONMENT_API_NAME_MAP[
    ENVIRONMENT_ABBREVIATED_NAME_MAP[environmentFromApiUrl(url)]
  ];

export const getEnvironmentFromSubdomain = (url: string): Environment =>
  environmentFromApiUrl(url);

type ActiveEnvironment = {
  api: ApiName;
  name: ApiName;
};

export const ACTIVE_ENVIRONMENT: ActiveEnvironment = {
  api: getApiFromSubdomain(Config[ConfigKeys.ApiUrl]),
  name: ENVIRONMENT_ABBREVIATED_NAME_MAP[
    getEnvironmentFromSubdomain(Config[ConfigKeys.ApiUrl])
  ],
};
